import React, { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { Role } from '@kcdash/shared';
import { Modal } from '../components/Modal.js';
import { ShieldCheck, Plus, Trash2, User, Key, Mail } from 'lucide-react';

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // New user form state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(Role.VIEWER);
  const [actionLoading, setActionLoading] = useState(false);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await api.getUsers();
      setUsers(data);
    } catch (err: any) {
      alert(`Failed to load users: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await api.createUser({ username, email, password, role });
      setIsCreateOpen(false);
      setUsername('');
      setEmail('');
      setPassword('');
      await loadUsers();
    } catch (err: any) {
      alert(`Failed to create user: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: Role) => {
    try {
      await api.updateUser(userId, { role: newRole });
      await loadUsers();
    } catch (err: any) {
      alert(`Failed to update role: ${err.message}`);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to remove user '${userName}'?`)) return;
    try {
      await api.deleteUser(userId);
      await loadUsers();
    } catch (err: any) {
      alert(`Failed to delete user: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutral-100">Users &amp; Role-Based Access Control</h2>
          <p className="text-xs text-neutral-400 mt-1 font-mono">
            Granular permissions management for VPS administrators and developers
          </p>
        </div>

        <button
          onClick={() => setIsCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>New User</span>
        </button>
      </div>

      <div className="bg-panel-card border border-panel-border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-panel-subtle/80 border-b border-panel-border text-neutral-400 uppercase select-none">
              <tr>
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Last Login</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-panel-border/50 text-neutral-300">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-neutral-500">
                    Loading users...
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-panel-cardHover transition">
                    <td className="px-5 py-3.5 flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-brand-950 border border-brand-800 flex items-center justify-center text-brand-400 font-bold">
                        {u.username.substring(0, 2).toUpperCase()}
                      </div>
                      <span className="font-semibold text-neutral-100">{u.username}</span>
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400">{u.email}</td>
                    <td className="px-5 py-3.5">
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value as Role)}
                        className="px-2 py-1 rounded bg-panel-input border border-panel-border text-xs text-brand-400 font-bold font-mono focus:outline-none"
                      >
                        <option value={Role.OWNER}>OWNER</option>
                        <option value={Role.ADMIN}>ADMIN</option>
                        <option value={Role.DEVELOPER}>DEVELOPER</option>
                        <option value={Role.VIEWER}>VIEWER</option>
                      </select>
                    </td>
                    <td className="px-5 py-3.5 text-neutral-500">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => handleDeleteUser(u.id, u.username)}
                        title="Delete user"
                        className="p-1.5 rounded-lg hover:bg-red-950/60 text-neutral-400 hover:text-red-400 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New User Account"
        maxWidth="sm"
      >
        <form onSubmit={handleCreateUser} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
              Username
            </label>
            <input
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
              Email Address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
              Initial Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
              Assigned Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
            >
              <option value={Role.VIEWER}>VIEWER (Read-Only Monitoring)</option>
              <option value={Role.DEVELOPER}>DEVELOPER (Files, Console &amp; Gamemodes)</option>
              <option value={Role.ADMIN}>ADMIN (Full Server Control)</option>
              <option value={Role.OWNER}>OWNER (Unrestricted Access)</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-panel-border">
            <button
              type="button"
              onClick={() => setIsCreateOpen(false)}
              className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={actionLoading}
              className="px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white"
            >
              Create Account
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
